import * as React from 'react';
import {
  ComboBox as RACComboBox,
  Input,
  Button as RACButton,
  ListBox,
  ListBoxItem,
  Popover,
} from 'react-aria-components';
import { DownSmallLine, CheckLine } from '@mingcute/react';

import { cn } from '../lib/cn';

/**
 * Klaret ComboBox (handoff §10.3).
 * Trigger = input-style 36 px, r2, --tvl-border-input, --tvl-raised.
 * Filter input pinned top; empty result footnote --tvl-ink-3.
 * Options 32 px, r2, 8 px padding; selected = body-medium + claret check + --tvl-selected.
 * Focus = global 2 px ink outline. No accent ring (text field gets accent border on focus).
 */

export interface ComboboxOption {
  value: string;
  label: string;
  disabled?: boolean;
  group?: string;
}

export interface ComboboxProps {
  options: ComboboxOption[];
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  invalid?: boolean;
  className?: string;
  id?: string;
  /**
   * Custom filter function. Accepts either the new form
   * `(option: ComboboxOption, inputValue: string) => boolean`
   * or the legacy cmdk form `(value: string, search: string) => number`
   * (return value truthy = include). Both shapes are accepted.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filter?: (optionOrValue: any, inputValue: string) => any;
}

export const Combobox = React.forwardRef<HTMLElement, ComboboxProps>(
  (
    {
      options,
      value,
      onValueChange,
      placeholder = 'Select…',
      searchPlaceholder = 'Search…',
      emptyMessage = 'No results found.',
      disabled,
      invalid,
      className,
      id,
      filter,
    },
    ref,
  ) => {
    const defaultFilter = React.useCallback(
      (item: ComboboxOption, inputValue: string) =>
        item.label.toLowerCase().includes(inputValue.toLowerCase()),
      [],
    );

    const activeFilter = filter ?? defaultFilter;

    const [inputValue, setInputValue] = React.useState('');

    const filteredOptions = React.useMemo(
      () => options.filter((o) => activeFilter(o, inputValue)),
      [options, activeFilter, inputValue],
    );

    return (
      <RACComboBox<ComboboxOption>
        ref={ref as never}
        id={id}
        isDisabled={disabled}
        aria-invalid={invalid || undefined}
        selectedKey={value ?? null}
        onSelectionChange={(key) => {
          if (key !== null) {
            onValueChange?.(String(key));
            setInputValue('');
          }
        }}
        inputValue={inputValue}
        onInputChange={setInputValue}
        items={filteredOptions}
        className={cn('group relative flex w-full flex-col gap-0', className)}
        allowsCustomValue={false}
      >
        {/* Trigger row: typed input + chevron button */}
        <div
          className={cn(
            'flex h-9 w-full items-center rounded-[var(--tvl-r2)]',
            'border border-[var(--tvl-border-input)] bg-[var(--tvl-raised)]',
            'transition-colors duration-[var(--tvl-d-fast)]',
            'group-hover:border-[var(--tvl-ink-2)]',
            'group-focus-within:border-[var(--tvl-accent)]',
            'group-focus-within:shadow-[inset_0_0_0_1px_var(--tvl-accent)]',
            invalid && 'border-[var(--tvl-danger)]',
            disabled && 'cursor-not-allowed opacity-50',
          )}
        >
          <Input
            placeholder={placeholder}
            className={cn(
              'h-full flex-1 bg-transparent px-3 text-sm text-[var(--tvl-ink)]',
              'placeholder:text-[var(--tvl-ink-3)] outline-none',
              disabled && 'cursor-not-allowed',
            )}
          />
          <RACButton className="flex h-full w-9 shrink-0 items-center justify-center rounded-r-[var(--tvl-r2)] outline-none">
            <DownSmallLine size={16} color="var(--tvl-ink-3)" aria-hidden />
          </RACButton>
        </div>

        {/* Dropdown */}
        <Popover
          className={cn(
            'z-[var(--tvl-z-popover)] w-[--trigger-width] overflow-hidden',
            'rounded-[var(--tvl-r3)] border border-[var(--tvl-hairline)]',
            'bg-[var(--tvl-raised)] shadow-[var(--tvl-e2)]',
            'data-[entering]:animate-in data-[entering]:fade-in-0 data-[entering]:zoom-in-95',
            'data-[exiting]:animate-out data-[exiting]:fade-out-0 data-[exiting]:zoom-out-95',
          )}
          offset={4}
        >
          <ListBox<ComboboxOption>
            className="max-h-60 overflow-auto p-1 outline-none"
            renderEmptyState={() => (
              <div className="px-2 py-3 text-center text-xs text-[var(--tvl-ink-3)]">
                {emptyMessage}
              </div>
            )}
          >
            {(item) => (
              <ListBoxItem
                key={item.value}
                id={item.value}
                textValue={item.label}
                isDisabled={item.disabled}
                className={cn(
                  'flex h-8 w-full cursor-pointer select-none items-center justify-between',
                  'rounded-[var(--tvl-r2)] px-2 text-sm text-[var(--tvl-ink)] outline-none',
                  'hover:bg-[var(--tvl-wash-hover)]',
                  'data-[focused]:bg-[var(--tvl-wash-hover)]',
                  'data-[selected]:bg-[var(--tvl-selected)] data-[selected]:font-medium',
                  'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                )}
              >
                {({ isSelected }) => (
                  <>
                    <span className="flex-1 truncate">{item.label}</span>
                    {isSelected && (
                      <CheckLine size={14} color="var(--tvl-accent)" aria-hidden />
                    )}
                  </>
                )}
              </ListBoxItem>
            )}
          </ListBox>
        </Popover>
      </RACComboBox>
    );
  },
);
Combobox.displayName = 'Combobox';
